import DeliveryIcon from '@assets/icons/delivery.svg';
import i18n from '@/utils/i18n';
import PrinterIcon from '@assets/icons/printer.svg';
import React from 'react';
import TrashIcon from '@assets/icons/trash-small.svg';
import { CombinedActionSheet } from './combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { List } from './list';
import { ListOption } from './list-item';
import { SelectTagPrintBatchTemplate } from './tag-print-batch/select-tag-print-batch-template';
import { StyleProp, TextStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { TagPrintBatchDeleteConfirmation } from './tag-print-batch-delete-confirmation';
import { TagPrintBatchTemplateUnavailableOffline } from './tag-print-batch-template-unavailable-offline';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';

export enum TagPrintBatchListActionRoute {
  ChangeTemplate = 'CHANGE_TEMPLATE',
  Delete = 'DELETE',
  Print = 'PRINT',
  RequestDelivery = 'REQUEST_DELIVERY',
  Select = 'SELECT',
  TemplateUnavailableOffline = 'TEMPLATE_UNAVAILABLE_OFFLINE',
}

type TagPrintBatchListRouteOption = ListOption<{
  Icon: React.FC<SvgProps>;
  iconProps?: SvgProps;
  listTitleStyle?: StyleProp<TextStyle>;
}>;

interface Props {
  canUseSelectedPrintTemplateOffline: boolean;
  onClose: () => void;
  onDelete: () => void;
  onRequestDeliveryOfTags: () => Promise<void>;
  onSelectPrint: () => void;
  printTagBatchUUID: string;
  setSelectedOfflineTemplateUUID: React.Dispatch<
    React.SetStateAction<string | null>
  >;
}

export const TagPrintBatchListActionSheet: React.FC<Props> = ({
  canUseSelectedPrintTemplateOffline,
  onClose,
  onDelete,
  onRequestDeliveryOfTags,
  onSelectPrint,
  printTagBatchUUID,
  setSelectedOfflineTemplateUUID,
}) => {
  const [styles, theme] = useStyles();
  const batchDeliveryEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.batch_delivery_enabled;
  const isOfflineModeActive = useOfflineModeActive();

  const initialRouteOptions: TagPrintBatchListRouteOption[] = [
    {
      metadata: {
        Icon: PrinterIcon,
        iconProps: {
          stroke: theme.sk.color.icon.primary,
        },
      },
      title: i18n.t('print_batch'),
      value: TagPrintBatchListActionRoute.Print,
    },
  ];

  if (batchDeliveryEnabled) {
    initialRouteOptions.push({
      metadata: {
        Icon: DeliveryIcon,
        iconProps: {
          stroke: isOfflineModeActive
            ? theme.sk.color.icon.disabled
            : theme.sk.color.icon.primary,
        },
        listTitleStyle: isOfflineModeActive ? styles.disabledTitle : undefined,
      },
      title: i18n.t('request_delivery_of_tags'),
      value: TagPrintBatchListActionRoute.RequestDelivery,
    });
  }

  const routeOptions: TagPrintBatchListRouteOption[] = [
    ...initialRouteOptions,
    {
      metadata: {
        Icon: TrashIcon,
        iconProps: {
          stroke: isOfflineModeActive
            ? theme.sk.color.icon.disabled
            : theme.sk.color.icon.negative.primary,
        },
        listTitleStyle: isOfflineModeActive
          ? styles.disabledTitle
          : styles.deleteTitle,
      },
      title: i18n.t('delete_batch'),
      value: TagPrintBatchListActionRoute.Delete,
    },
  ];

  return (
    <CombinedActionSheet
      animateTransitions
      initialRoute={TagPrintBatchListActionRoute.Select}
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case TagPrintBatchListActionRoute.Delete:
            return (
              <TagPrintBatchDeleteConfirmation
                onClose={onClose}
                onDelete={onDelete}
                printTagBatchUUID={printTagBatchUUID}
              />
            );
          case TagPrintBatchListActionRoute.TemplateUnavailableOffline:
            return (
              <TagPrintBatchTemplateUnavailableOffline
                onAction={() => {
                  jumpTo(TagPrintBatchListActionRoute.ChangeTemplate);
                }}
              />
            );
          case TagPrintBatchListActionRoute.ChangeTemplate:
            return (
              <SelectTagPrintBatchTemplate
                initialTemplate={null}
                onSelectTemplate={(template) => {
                  if (template) {
                    setSelectedOfflineTemplateUUID(template.uuid);
                    onSelectPrint();
                  }
                }}
              />
            );
          case TagPrintBatchListActionRoute.Select:
            return (
              <List
                data={routeOptions}
                listItemTitleStyle={(item) => {
                  const { listTitleStyle } = item.metadata!;
                  return listTitleStyle;
                }}
                onSelect={(item) => {
                  switch (item.value) {
                    case TagPrintBatchListActionRoute.Delete:
                      if (!isOfflineModeActive) {
                        jumpTo(TagPrintBatchListActionRoute.Delete);
                      }
                      break;
                    case TagPrintBatchListActionRoute.Print:
                      if (
                        isOfflineModeActive &&
                        !canUseSelectedPrintTemplateOffline
                      ) {
                        jumpTo(
                          TagPrintBatchListActionRoute.TemplateUnavailableOffline,
                        );
                      } else {
                        onSelectPrint();
                      }
                      break;
                    case TagPrintBatchListActionRoute.RequestDelivery:
                      if (!isOfflineModeActive) {
                        onRequestDeliveryOfTags();
                      }
                      break;
                  }
                }}
                renderLeftComponent={({ metadata }) => {
                  const { Icon, iconProps } = metadata!;
                  return (
                    <Icon
                      height={24}
                      style={styles.icon}
                      width={24}
                      {...iconProps}
                    />
                  );
                }}
              />
            );
          // Printing is handled by TagPrintBatchConfirmationActionSheet
          case TagPrintBatchListActionRoute.Print:
          default:
            return null;
        }
      }}
      routes={{
        [TagPrintBatchListActionRoute.Delete]: {
          previousKey: null,
        },
        [TagPrintBatchListActionRoute.Print]: {
          previousKey: null,
        },
        [TagPrintBatchListActionRoute.RequestDelivery]: {
          previousKey: null,
        },
        [TagPrintBatchListActionRoute.TemplateUnavailableOffline]: {
          previousKey: null,
        },
        [TagPrintBatchListActionRoute.ChangeTemplate]: {
          previousKey: null,
        },
        [TagPrintBatchListActionRoute.Select]: {
          contentStyle: styles.selectList,
          previousKey: null,
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  deleteTitle: {
    color: theme.sk.color.text.error,
  },
  disabledTitle: {
    color: theme.sk.color.text.disabled,
  },
  icon: {
    marginRight: 8,
  },
  selectList: {
    paddingHorizontal: 8,
  },
}));
